/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { LimitedAccessToFeaturesMessageBar } from 'src/core/components/LimitedAccessToFeaturesMessageBar';
import { Page } from 'src/core/components/Page';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { getEventLogger } from 'src/core/services/telemetry/logEventBuilder';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { PatternPageHeader } from 'src/luis/appShell/buildPane/patternsPage/components/PatternPageHeader';
import { PatternsFilter } from 'src/luis/appShell/buildPane/patternsPage/components/PatternsFilter';
import { patternsPageMessages } from 'src/luis/appShell/buildPane/patternsPage/patternsPageMessages';
import { Intent } from 'src/luis/modules/intents/Intent';
import { Pattern } from 'src/luis/modules/patterns/Pattern';
import { usePatterns } from 'src/luis/modules/patterns/usePatterns';
import { ModelType } from 'src/luis/types/ModelType';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { CreateEntityModal } from 'src/luis/verticals/createEntityModal/CreateEntityModal';
import { PatternTableContainer } from 'src/luis/verticals/patternsTable/PatternTableContainer';
import { FiltersItem } from 'src/verticals/utterancesFilters/types/Filters';

const defaultModalStates = { isFiltersOpen: false, isEntityCreationModalHidden: true };

/**
 * Represents a page container component for patterns.
 */
export const PatternsPage = observer(() => {
    const [{ entityName, roleName }, setEntityRoleNames] = React.useState<{ entityName: string; roleName: string }>({
        entityName: '',
        roleName: ''
    });
    const [modalsState, setModalsState] = useUpdateState(defaultModalStates);
    const [filteredPatternsLength, setFilteredPatternsLength] = React.useState<number>(0);
    const [intentsFiltersItems, setIntentsFiltersItems] = React.useState<FiltersItem[]>([]);
    const [entitiesFiltersItems, setEntitiesFiltersItems] = React.useState<FiltersItem[]>([]);
    const { localize } = useLocalization();
    const { dispatcher, rootStore } = useGlobalState();
    const { entities, intents } = usePatterns();
    const activitySource = 'PatternsPage';
    const eventLoggerRef = React.useRef(getEventLogger(activitySource));

    const defaultTracker = useProgressTracker({
        onComplete: () => {
            setModalsState({ isEntityCreationModalHidden: true });
        }
    });

    const appliedFilters = [...intentsFiltersItems.filter(item => item.isChecked), ...entitiesFiltersItems.filter(item => item.isChecked)];

    const { userRole, displayedUserRole } = rootStore.serviceKey;

    const { canWrite } = mapUserRoleToUserActions(userRole);

    /**
     * Sets all filters items to be unchecked.
     */
    const initializeFilters = () => {
        setEntitiesFiltersItems(
            entities.map<FiltersItem>(entity => ({ id: entity.id, name: entity.name, filterType: 'entities', isChecked: false }))
        );
        setIntentsFiltersItems(
            intents.map<FiltersItem>(intent => ({ id: intent.id, name: intent.name, filterType: 'intents', isChecked: false }))
        );
    };

    /**
     * Reflect the change occurred in the filter items.
     */
    const handleFiltersChange = (item: FiltersItem) => {
        if (item.filterType === 'intents') {
            setIntentsFiltersItems(intentsFiltersItems.map(filterItem => invertFilterItem(filterItem, item)));
        } else {
            setEntitiesFiltersItems(entitiesFiltersItems.map(filterItem => invertFilterItem(filterItem, item)));
        }
    };

    /**
     * Creates a new pattern with the given pattern properties.
     *
     * @param pattern The new pattern to be created.
     */
    const handleCreateNewPattern = (pattern: Pattern) => {
        dispatcher.dispatch('createPattern', {
            text: pattern.text,
            intentId: pattern.labeledIntent.id as string,
            tracker: defaultTracker,
            toastData: {
                running: { title: localize(patternsPageMessages.createPatternToastStart) },
                completed: { title: localize(patternsPageMessages.createPatternToastSuccess) },
                failed: { title: localize(patternsPageMessages.createPatternToastFailed) }
            }
        });
    };

    /**
     * Callback for when after the entity is created
     *
     * @param entity The entity created.
     */
    const onEntityCreated = (entity: EntityStore) => {
        setModalsState({ isEntityCreationModalHidden: true });

        if (roleName) {
            onAddNewRole(entity.name, roleName);
        }
        eventLoggerRef.current.end('CreateEntityDialog');
    };

    /**
     * Updates existing patterns with the given pattern properties.
     *
     * @param selectedPatterns The new patterns properties to be updated.
     * @param intent The new labeled intent for the updated patterns.
     */
    const handleUpdatePatterns = (selectedPatterns: Pattern[], intent: Intent) => {
        dispatcher.dispatch('updatePatterns', {
            patterns: selectedPatterns.map(pattern => ({
                id: pattern.id,
                text: pattern.text,
                intentId: intent.id as string
            })),
            tracker: defaultTracker,
            toastData: {
                running: { title: localize(patternsPageMessages.updatePatternToastStart) },
                completed: { title: localize(patternsPageMessages.updatePatternToastSuccess) },
                failed: { title: localize(patternsPageMessages.updatePatternToastFailed) }
            }
        });
    };

    /**
     * Callback function to handle the create new role action.
     */
    const onAddNewRole = (roleEntityName: string, rName: string) => {
        const entityId = rootStore.currentLuisAppStore.currentAppVersionStore.entityStoreHouse.findByName(roleEntityName).id;
        dispatcher.dispatch('addEntityRole', { entityId, roleName: rName });
    };

    /**
     * Callback function to handle the create new entity action.
     */
    const onCreateNewEntity = (eName: string) => {
        setEntityRoleNames({ entityName: eName, roleName: null });
        setModalsState({ isEntityCreationModalHidden: false });
        eventLoggerRef.current.start('CreateEntityDialog');
    };

    /**
     * Callback function to handle the create new entity and role action.
     */
    const onCreateNewEntityAndRole = (eName: string, rName: string) => {
        setEntityRoleNames({ entityName: eName, roleName: rName });
        setModalsState({ isEntityCreationModalHidden: false });
        eventLoggerRef.current.start('CreateEntityDialog');
    };

    /*
     * Closes entity creation modal and logEvent for the modal dismissal.
     *
     */
    const dismissCreateEntityModal = () => {
        setModalsState({ isEntityCreationModalHidden: true });
        eventLoggerRef.current.end('CreateEntityDialog');
        eventLoggerRef.current.log('CreateEntityDialogDismissed');
    };

    return (
        <Page size="container-fluid" pageName="PatternsPage" title="Patterns">
            <EditableTitle title={localize(patternsPageMessages.title)} description={localize(patternsPageMessages.titleDescription)} />

            <Stack tokens={{ childrenGap: 10 }}>
                {!canWrite && <LimitedAccessToFeaturesMessageBar displayedUserRole={displayedUserRole} />}

                <PatternPageHeader
                    intents={intents}
                    entities={entities}
                    onCreateNewPattern={handleCreateNewPattern}
                    onCreateNewEntity={onCreateNewEntity}
                    onCreateNewRole={onAddNewRole}
                    onCreateNewEntityAndRole={onCreateNewEntityAndRole}
                    userRole={userRole}
                    displayedUserRole={displayedUserRole}
                />

                <PatternTableContainer
                    appliedFilters={appliedFilters}
                    loggerActivitySource={activitySource}
                    onReassignIntent={handleUpdatePatterns}
                    onFilterPattern={() => setModalsState({ isFiltersOpen: true })}
                    setFilteredPatternsLength={setFilteredPatternsLength}
                    initializeFilters={initializeFilters}
                    areAdvancedOptionsEnabled
                />

                <PatternsFilter
                    isOpen={modalsState.isFiltersOpen}
                    resultsCount={appliedFilters.length ? filteredPatternsLength : 0}
                    onLastBladeDismiss={() => setModalsState({ isFiltersOpen: false })}
                    entities={entitiesFiltersItems}
                    intents={intentsFiltersItems}
                    onSelectionChange={handleFiltersChange}
                    onClearAllClick={initializeFilters}
                    areFiltersApplied={Boolean(appliedFilters.length)}
                />
            </Stack>

            <CreateEntityModal
                entityName={entityName}
                allowedModelTypes={[ModelType.Simple, ModelType.List, ModelType.Regex, ModelType.PatternAny]}
                hidden={modalsState.isEntityCreationModalHidden}
                onSubmit={onEntityCreated}
                onDismiss={dismissCreateEntityModal}
            />
        </Page>
    );
});

/**
 * Inverts the filter item if it matches the target one.
 *
 * @param filterItem The filter item to be inverted.
 * @param targetItem The target filter item to match with.
 */
const invertFilterItem = (filterItem: FiltersItem, targetItem: FiltersItem): FiltersItem => {
    if (filterItem.id === targetItem.id) {
        return { ...filterItem, isChecked: !filterItem.isChecked };
    }

    return filterItem;
};
